import {
  PAYMENT_GROUPS,
  PaymentGroup,
  PaymentGroupEnum,
  TPaymentListItem
} from '../types/ManagePayments';
import { TScheduledBankPaymentGroupState } from '@/features/payments/types/BatchPayment';

export type TPaymentUpdate = Partial<TPaymentListItem> & Pick<TPaymentListItem, 'id'>;

/**
 * Applies a known state change to one row across the tab-bucketed payments map, moving it to
 * whichever tab its new state belongs to. Pure so the move-vs-patch-in-place decision can be
 * tested without rendering the hook that owns the surrounding state.
 */
export const applyPaymentUpdateToGroups = (
  groups: Record<PaymentGroup, TPaymentListItem[]>,
  patch: TPaymentUpdate,
  groupByState: Record<TScheduledBankPaymentGroupState, PaymentGroup>
): Record<PaymentGroup, TPaymentListItem[]> => {
  const sourceGroup = PAYMENT_GROUPS.find((group) =>
    groups[group].some((item) => item.id === patch.id)
  );
  const existing = sourceGroup && groups[sourceGroup].find((item) => item.id === patch.id);
  if (!sourceGroup || !existing) return groups;

  const updated: TPaymentListItem = { ...existing, ...patch };
  // A force-completed batch always belongs in Batch History regardless of its underlying state
  // (which can stay FAILED/COMPLETED_WITH_FAILURES even once force-completed).
  const targetGroup = updated.forceCompleted
    ? PaymentGroupEnum.BATCH_HISTORY
    : groupByState[updated.state];
  if (!targetGroup) return groups;

  if (targetGroup === sourceGroup) {
    return {
      ...groups,
      [sourceGroup]: groups[sourceGroup].map((item) => (item.id === patch.id ? updated : item))
    };
  }

  return {
    ...groups,
    [sourceGroup]: groups[sourceGroup].filter((item) => item.id !== patch.id),
    [targetGroup]: [updated, ...groups[targetGroup]]
  };
};

/**
 * Removes one row from whichever tab it's currently in - for a successful delete, where the row
 * no longer exists anywhere rather than moving to a different tab.
 */
export const removePaymentFromGroups = (
  groups: Record<PaymentGroup, TPaymentListItem[]>,
  id: string
): Record<PaymentGroup, TPaymentListItem[]> => {
  const sourceGroup = PAYMENT_GROUPS.find((group) => groups[group].some((item) => item.id === id));
  if (!sourceGroup) return groups;

  return {
    ...groups,
    [sourceGroup]: groups[sourceGroup].filter((item) => item.id !== id)
  };
};
